import { Node, mergeAttributes } from '@tiptap/core'

export const TiptapVideoResizeExtention = Node.create({
  name: 'video',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      controls: {
        default: true,
      },
      width: {
        default: 480,
      },
      height: {
        default: 270,
      },
      align: {
        default: 'left',
        parseHTML: (element) => {
          const containerStyle = element.parentElement?.style?.cssText || ''
          if (containerStyle.includes('margin: 0 auto')) return 'center'
          if (containerStyle.includes('margin: 0 0 0 auto')) return 'right'
          return 'left'
        },
        renderHTML: ({ align }) => {
          return {
            'data-align': align,
            align: align,
          }
        },
      },
      style: {
        default: 'width: 480px; height: 270px; cursor: pointer;',
        parseHTML: (element) => {
          const width = element.getAttribute('width')
          const height = element.getAttribute('height')
          return width && height
            ? `width: ${width}px; height: ${height}px; cursor: pointer;`
            : `${element.style.cssText}`
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'video',
        getAttrs: (element) => {
          // Get align from data-align attribute first
          const align = element.getAttribute('data-align') || 'left'
          return {
            src: element.getAttribute('src'),
            width: element.getAttribute('width') || 480,
            height: element.getAttribute('height') || 270,
            align: align,
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const { align = 'left', ...videoAttrs } = HTMLAttributes
    const justifyContent =
      align === 'center'
        ? 'center'
        : align === 'right'
          ? 'flex-end'
          : 'flex-start'

    return [
      'div',
      {
        style: `display: flex; justify-content: ${justifyContent}; width: 100%;`,
      },
      [
        'video',
        mergeAttributes(videoAttrs, {
          controls: true,
          'data-align': align, // Store alignment for parsing
        }),
      ],
    ]
  },

  addCommands() {
    return {
      setVideo:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          })
        },
    }
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const {
        view,
        options: { editable },
      } = editor
      const { style } = node.attrs
      const $wrapper = document.createElement('div')
      const $container = document.createElement('div')
      const $video = document.createElement('video')
      const iconStyle = 'width: 24px; height: 24px; cursor: pointer;'

      const getContainerBaseStyle = () => {
        const currentAlign = node.attrs.align || 'left'
        const justifyContent =
          currentAlign === 'center'
            ? 'center'
            : currentAlign === 'right'
              ? 'flex-end'
              : 'flex-start'
        return `${style} display: flex; justify-content: ${justifyContent}; width: 100%;`
      }

      const dispatchNodeView = () => {
        if (typeof getPos === 'function') {
          const newAttrs = {
            ...node.attrs, // Preserve align attribute
            style: `${$video.style.cssText}`,
            width: parseInt($video.style.width) || node.attrs.width,
            height: parseInt($video.style.height) || node.attrs.height,
          }
          view.dispatch(view.state.tr.setNodeMarkup(getPos(), null, newAttrs))
          // Update container alignment after dispatch
          $container.style.justifyContent =
            newAttrs.align === 'center'
              ? 'center'
              : newAttrs.align === 'right'
                ? 'flex-end'
                : 'flex-start'
        }
      }

      const paintPositionController = () => {
        const $positionController = document.createElement('div')

        const $leftController = document.createElement('img')
        const $centerController = document.createElement('img')
        const $rightController = document.createElement('img')

        const controllerMouseOver = (e) => {
          e.target.style.opacity = 0.3
        }

        const controllerMouseOut = (e) => {
          e.target.style.opacity = 1
        }

        $positionController.setAttribute(
          'style',
          'position: absolute; top: 0%; left: 50%; width: 100px; height: 25px; z-index: 999; background-color: rgba(255, 255, 255, 0.7); border-radius: 4px; border: 2px solid #6C6C6C; cursor: pointer; transform: translate(-50%, -50%); display: flex; justify-content: space-between; align-items: center; padding: 0 10px;',
        )

        $leftController.setAttribute(
          'src',
          'https://fonts.gstatic.com/s/i/short-term/release/materialsymbolsoutlined/format_align_left/default/20px.svg',
        )
        $leftController.setAttribute('style', iconStyle)
        $leftController.addEventListener('mouseover', controllerMouseOver)
        $leftController.addEventListener('mouseout', controllerMouseOut)

        $centerController.setAttribute(
          'src',
          'https://fonts.gstatic.com/s/i/short-term/release/materialsymbolsoutlined/format_align_center/default/20px.svg',
        )
        $centerController.setAttribute('style', iconStyle)
        $centerController.addEventListener('mouseover', controllerMouseOver)
        $centerController.addEventListener('mouseout', controllerMouseOut)

        $rightController.setAttribute(
          'src',
          'https://fonts.gstatic.com/s/i/short-term/release/materialsymbolsoutlined/format_align_right/default/20px.svg',
        )
        $rightController.setAttribute('style', iconStyle)
        $rightController.addEventListener('mouseover', controllerMouseOver)
        $rightController.addEventListener('mouseout', controllerMouseOut)

        $leftController.addEventListener('click', () => {
          const newAttrs = {
            ...node.attrs,
            align: 'left',
            style: `${$video.style.cssText}`,
            width: parseInt($video.style.width) || node.attrs.width,
            height: parseInt($video.style.height) || node.attrs.height,
          }
          view.dispatch(view.state.tr.setNodeMarkup(getPos(), null, newAttrs))
          // Update container to show immediate visual feedback
          $container.style.justifyContent = 'flex-start'
        })
        $centerController.addEventListener('click', () => {
          const newAttrs = {
            ...node.attrs,
            align: 'center',
            style: `${$video.style.cssText}`,
            width: parseInt($video.style.width) || node.attrs.width,
            height: parseInt($video.style.height) || node.attrs.height,
          }
          view.dispatch(view.state.tr.setNodeMarkup(getPos(), null, newAttrs))
          // Update container to show immediate visual feedback
          $container.style.justifyContent = 'center'
        })
        $rightController.addEventListener('click', () => {
          const newAttrs = {
            ...node.attrs,
            align: 'right',
            style: `${$video.style.cssText}`,
            width: parseInt($video.style.width) || node.attrs.width,
            height: parseInt($video.style.height) || node.attrs.height,
          }
          view.dispatch(view.state.tr.setNodeMarkup(getPos(), null, newAttrs))
          // Update container to show immediate visual feedback
          $container.style.justifyContent = 'flex-end'
        })

        $positionController.appendChild($leftController)
        $positionController.appendChild($centerController)
        $positionController.appendChild($rightController)

        $container.appendChild($positionController)
      }

      $wrapper.setAttribute('style', `display: flex;`)
      $wrapper.appendChild($container)

      // Apply initial alignment using flexbox
      $container.setAttribute('style', getContainerBaseStyle())
      $container.appendChild($video)

      // Set video attributes
      $video.controls = true
      Object.entries(node.attrs).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          key === 'style' ||
          key === 'align'
        )
          return
        $video.setAttribute(key, value)
      })

      if (!editable) return { dom: $container }

      const isMobile = document.documentElement.clientWidth < 768
      const dotPosition = isMobile ? '-8px' : '-4px'
      const dotsPosition = [
        `top: ${dotPosition}; left: ${dotPosition}; cursor: nwse-resize;`,
        `top: ${dotPosition}; right: ${dotPosition}; cursor: nesw-resize;`,
        `bottom: ${dotPosition}; left: ${dotPosition}; cursor: nesw-resize;`,
        `bottom: ${dotPosition}; right: ${dotPosition}; cursor: nwse-resize;`,
      ]

      let isResizing = false
      let startX, startY, startWidth, startHeight

      $container.addEventListener('click', (e) => {
        // Remove remaining dots and position controller
        const isMobile = document.documentElement.clientWidth < 768
        isMobile && document.querySelector('.ProseMirror-focused')?.blur()

        if ($container.childElementCount > 3) {
          for (let i = 0; i < 5; i++) {
            $container.removeChild($container.lastChild)
          }
        }

        paintPositionController()

        // Add editing styles while preserving alignment
        $container.style.position = 'relative'
        $container.style.border = '1px dashed #6C6C6C'
        $container.style.cursor = 'pointer'

        Array.from({ length: 4 }, (_, index) => {
          const $dot = document.createElement('div')
          $dot.setAttribute(
            'style',
            `position: absolute; width: ${isMobile ? 16 : 9}px; height: ${isMobile ? 16 : 9}px; border: 1.5px solid #6C6C6C; border-radius: 50%; ${dotsPosition[index]}`,
          )

          $dot.addEventListener('mousedown', (e) => {
            e.preventDefault()
            isResizing = true
            startX = e.clientX
            startY = e.clientY
            startWidth = $container.offsetWidth
            startHeight = $container.offsetHeight

            const onMouseMove = (e) => {
              if (!isResizing) return

              const deltaX =
                index % 2 === 0 ? -(e.clientX - startX) : e.clientX - startX
              const deltaY =
                index < 2 ? -(e.clientY - startY) : e.clientY - startY

              const newWidth = Math.max(100, startWidth + deltaX)
              const newHeight = Math.max(100, startHeight + deltaY)

              $container.style.width = newWidth + 'px'
              $container.style.height = newHeight + 'px'
              $video.style.width = newWidth + 'px'
              $video.style.height = newHeight + 'px'
            }

            const onMouseUp = () => {
              if (isResizing) {
                isResizing = false
              }
              dispatchNodeView()

              document.removeEventListener('mousemove', onMouseMove)
              document.removeEventListener('mouseup', onMouseUp)
            }

            document.addEventListener('mousemove', onMouseMove)
            document.addEventListener('mouseup', onMouseUp)
          })

          $dot.addEventListener(
            'touchstart',
            (e) => {
              e.cancelable && e.preventDefault()
              isResizing = true
              startX = e.touches[0].clientX
              startY = e.touches[0].clientY
              startWidth = $container.offsetWidth
              startHeight = $container.offsetHeight

              const onTouchMove = (e) => {
                if (!isResizing) return

                const deltaX =
                  index % 2 === 0
                    ? -(e.touches[0].clientX - startX)
                    : e.touches[0].clientX - startX
                const deltaY =
                  index < 2
                    ? -(e.touches[0].clientY - startY)
                    : e.touches[0].clientY - startY

                const newWidth = Math.max(100, startWidth + deltaX)
                const newHeight = Math.max(100, startHeight + deltaY)

                $container.style.width = newWidth + 'px'
                $container.style.height = newHeight + 'px'
                $video.style.width = newWidth + 'px'
                $video.style.height = newHeight + 'px'
              }

              const onTouchEnd = () => {
                if (isResizing) {
                  isResizing = false
                }
                dispatchNodeView()

                document.removeEventListener('touchmove', onTouchMove)
                document.removeEventListener('touchend', onTouchEnd)
              }

              document.addEventListener('touchmove', onTouchMove)
              document.addEventListener('touchend', onTouchEnd)
            },
            { passive: false },
          )
          $container.appendChild($dot)
        })
      })

      document.addEventListener('click', (e) => {
        const $target = e.target
        const isClickInside =
          $container.contains($target) || $target.style.cssText === iconStyle

        if (!isClickInside) {
          // Restore base style with proper alignment
          $container.setAttribute('style', getContainerBaseStyle())

          if ($container.childElementCount > 3) {
            for (let i = 0; i < 5; i++) {
              $container.removeChild($container.lastChild)
            }
          }
        }
      })

      return {
        dom: $wrapper,
      }
    }
  },
})
